// MyAppsSettings — /app/settings/apps page. Lets the calling user
// manage their HUB app memberships. Two tabs:
//   - Active: cards with [Configure] and [Deactivate] for each active
//     or pending_onboarding or suspended membership.
//   - Available: cards with [Activate] (or [Reactivate]) for each app
//     in the catalog that the user is NOT yet active in.
//
// Strictly Preview-gated by REACT_APP_HUB_NAV_ENABLED. Routes that
// reach this component without the flag are caught by App.js (which
// only registers the route when the flag is on).

import React, { useState } from "react";
import { Settings2, Loader2, Trash2, CheckCircle2, AlertTriangle, XCircle, Sparkles } from "lucide-react";
import { useLang } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { useHubI18n } from "@/i18n/hubI18n";
import { useAppMemberships } from "@/components/hub/useAppMemberships";
import OnboardingWizard from "@/components/hub/OnboardingWizard";
import PublicLayout from "@/components/layout/PublicLayout";
import { api } from "@/lib/api";

const formatDate = (iso, lang) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(lang === "es" ? "es-ES" : "en-US",
      { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
};

const ActiveCard = ({ membership, i18n, onConfigure, onDeactivate, lang }) => {
  // TEAM-OS-MY-APPS-PLAYER-ROUTING-AUDIT-1 (Feb 2026) — synthetic player
  // entry: read-only card pointing to the in-session dashboard. No
  // Configure/Deactivate buttons (system-controlled · not user-managed).
  if (membership.is_synthetic_player_entry) {
    return (
      <article
        className="border border-emerald-300 bg-emerald-50/60 rounded-xl p-4 flex flex-col gap-3"
        data-testid={`my-apps-card-${membership.app_key}`}
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              {i18n.appLabels[membership.app_key] || membership.app_key}
            </h3>
            <div className="text-xs text-emerald-700 mt-1 flex items-center gap-1.5 font-medium">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" /> {i18n.statusActive}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-1">
          <a
            href={membership.href || "/app"}
            className="px-3 py-1.5 rounded-md border border-emerald-400 bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition"
            data-testid={`my-apps-card-open-${membership.app_key}`}
          >
            {i18n.btnConfigure}
          </a>
        </div>
      </article>
    );
  }
  const statusIcon =
    membership.status === "active" ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> :
    membership.status === "pending_onboarding" ? <AlertTriangle className="w-4 h-4 text-amber-600" /> :
    <XCircle className="w-4 h-4 text-red-600" />;
  const statusLabel =
    membership.status === "active" ? i18n.statusActive :
    membership.status === "pending_onboarding" ? i18n.statusPending :
    i18n.statusSuspended;
  return (
    <article
      className="border border-slate-200 bg-white rounded-xl p-4 flex flex-col gap-3"
      data-testid={`my-apps-card-${membership.app_key}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-900">
            {i18n.appLabels[membership.app_key] || membership.app_key}
          </h3>
          <div className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
            {statusIcon} {statusLabel}
          </div>
        </div>
        <div className="text-end text-[11px] text-slate-500 leading-snug">
          <div>{i18n.joinedOn}: {formatDate(membership.joined_at, lang)}</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-1">
        <button
          type="button"
          onClick={() => onConfigure(membership.app_key)}
          className="px-3 py-1.5 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-800 text-xs font-medium hover:bg-emerald-100 transition"
          data-testid={`my-apps-card-configure-${membership.app_key}`}
        >
          <Settings2 className="w-3.5 h-3.5 inline me-1" /> {i18n.btnConfigure}
        </button>
        <button
          type="button"
          onClick={() => onDeactivate(membership.app_key)}
          className="px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-700 text-xs font-medium hover:bg-slate-50 transition"
          data-testid={`my-apps-card-deactivate-${membership.app_key}`}
        >
          <Trash2 className="w-3.5 h-3.5 inline me-1" /> {i18n.btnDeactivate}
        </button>
      </div>
    </article>
  );
};

const AvailableCard = ({ appCatalog, isRevoked, i18n, onActivate }) => (
  <article
    className="border border-slate-200 bg-white rounded-xl p-4 flex flex-col gap-3"
    data-testid={`my-apps-available-card-${appCatalog.app_key}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <h3 className="text-base font-semibold text-slate-900">
          {i18n.appLabels[appCatalog.app_key] || appCatalog.app_key}
        </h3>
        {appCatalog.required_fields?.length > 0 && (
          <div className="text-[11px] text-slate-500 mt-1">
            {appCatalog.required_fields.filter(f => f.tier === "hard_required").length} {i18n.wizardRequired.toLowerCase()}
          </div>
        )}
      </div>
      <Sparkles className="w-4 h-4 text-slate-300" />
    </div>
    <button
      type="button"
      onClick={() => onActivate(appCatalog.app_key)}
      className="self-start px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 transition"
      data-testid={`my-apps-card-activate-${appCatalog.app_key}`}
    >
      {isRevoked ? i18n.btnReactivate : i18n.btnActivate}
    </button>
  </article>
);

// Phase A6-prod (Feb 2026) — Apps que son admin-only en Producción.
// La membresía puede existir en DB (data legacy de Preview, lazy
// backstop activado por error, migración previa) pero la UI debe
// ocultarla a usuarios non-admin. Defensa en profundidad: el backend
// ya rechaza con 403 cualquier acción real sobre estas apps (ver
// `backend/app/modules/racket_lab/routes.py:78` _is_admin_or_staff
// bypass + HUB_REQUIRE_RACKET_LAB_MEMBERSHIP gate). Aquí simplemente
// evitamos renderizar tarjetas o botones que el usuario no debería
// poder usar. Para añadir otra app a la regla: incluir su `app_key`.
const ADMIN_ONLY_APPS = new Set(["racket_lab"]);
// TEAM-OS-MY-APPS-PLAYER-ROUTING-AUDIT-1 (Feb 2026) — `team_core` is the
// system passport (identity/dashboard/permissions/rewards). It is base
// of TEAM OS, not a user-managed app, so we hide it from My Apps for
// non-admin/non-staff users (Option A · José/Lumia preference). Also
// hide `marketplace` for `profile_key=player` to avoid the legacy
// catalog entry leaking into the player flow; the player sees a
// synthetic `racket_lab` ACTIVE row instead, routed to the dashboard
// section where the Racket Lab preview lives (avoids exposing the
// public landing inside an authenticated session).
const SYSTEM_APPS = new Set(["team_core"]);
const PLAYER_HIDDEN_APPS = new Set(["marketplace"]);

const MyAppsSettings = () => {
  const { lang } = useLang();
  const { user } = useAuth();
  const i18n = useHubI18n(lang);
  const [tab, setTab] = useState("active");
  const [wizardApp, setWizardApp] = useState(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);
  const [busy, setBusy] = useState(false);
  const { memberships, catalog, catalogDisabled, isLoading, refresh } = useAppMemberships(true);

  if (!user) {
    // Guard rail; ProtectedRoute already handles this but keep defensive.
    return null;
  }

  // Phase A6-prod (Feb 2026) — non-admin/staff users never see admin-only
  // apps in this screen, regardless of any legacy membership that may
  // still live in the `app_memberships` collection.
  const isAdminOrStaff = user.role === "admin" || user.role === "staff";
  const isPlayer = !isAdminOrStaff && user.profile_key === "player";
  const hiddenForRole = (appKey) => {
    if (!isAdminOrStaff && SYSTEM_APPS.has(appKey)) return true;
    if (!isAdminOrStaff && ADMIN_ONLY_APPS.has(appKey)) return true;
    if (isPlayer && PLAYER_HIDDEN_APPS.has(appKey)) return true;
    return false;
  };
  const visibleMemberships = (memberships || []).filter(
    (m) => !hiddenForRole(m.app_key)
  );
  const visibleCatalog = (catalog || []).filter(
    (c) => !hiddenForRole(c.app_key)
  );

  const activeList = visibleMemberships.filter(
    (m) => m.status === "active" || m.status === "pending_onboarding" || m.status === "suspended"
  );
  // RACKET-LAB-PLAYER-EQUIPMENT-UX-3 (Feb 2026) — Player profile sees a
  // single synthetic Racket Lab entry that links to the authenticated
  // player dashboard's Racket Lab section. Any legacy `racket_lab`
  // membership row in the DB is intentionally replaced for this view to
  // avoid the public landing leaking into the VIP demo.
  if (isPlayer) {
    activeList.length = 0;
    activeList.push({
      id: "synthetic-racket-lab-player",
      app_key: "racket_lab",
      status: "active",
      is_synthetic_player_entry: true,
      href: "/app#player-racket-lab",
    });
  }
  const activeKeys = new Set(activeList.map((m) => m.app_key));
  const availableList = visibleCatalog.filter((c) => !activeKeys.has(c.app_key));
  // Build a "revoked" lookup so the [Activate] button can show "Reactivate".
  const revokedKeys = new Set(
    visibleMemberships.filter((m) => m.status === "revoked").map((m) => m.app_key)
  );

  const handleConfigure = (appKey) => setWizardApp(appKey);

  const handleActivate = async (appKey) => {
    setBusy(true);
    try {
      await api.post("/app-memberships/upsert", {
        app_key: appKey,
        source: "self_signup",
      });
      await refresh();
      setWizardApp(appKey);
    } catch {
      // Surface a soft error; toast handled via i18n at the top of the page.
    } finally {
      setBusy(false);
    }
  };

  const handleDeactivate = (appKey) => setConfirmDeactivate(appKey);
  const confirmDeactivateNow = async () => {
    if (!confirmDeactivate) return;
    setBusy(true);
    try {
      await api.delete(`/app-memberships/me/${confirmDeactivate}`);
      await refresh();
    } finally {
      setBusy(false);
      setConfirmDeactivate(null);
    }
  };

  return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6" data-testid="my-apps-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{i18n.settingsPageTitle}</h1>
        <p className="text-sm text-slate-600 mt-1">{i18n.settingsPageSub}</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          type="button"
          onClick={() => setTab("active")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
            tab === "active" ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-900"
          }`}
          data-testid="my-apps-tab-active"
        >
          {i18n.tabActive} ({activeList.length})
        </button>
        <button
          type="button"
          onClick={() => setTab("available")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
            tab === "available" ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-900"
          }`}
          data-testid="my-apps-tab-available"
        >
          {i18n.tabAvailable} ({availableList.length})
        </button>
      </div>

      {isLoading && (
        <div className="text-sm text-slate-500 flex items-center gap-2" data-testid="my-apps-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> {i18n.loading}
        </div>
      )}

      {!isLoading && tab === "active" && (
        <div className="space-y-3" data-testid="my-apps-active-list">
          {activeList.length === 0 ? (
            <div className="text-sm text-slate-500" data-testid="my-apps-active-empty">{i18n.emptyActive}</div>
          ) : (
            activeList.map((m) => (
              <ActiveCard
                key={m.app_key}
                membership={m}
                i18n={i18n}
                lang={lang}
                onConfigure={handleConfigure}
                onDeactivate={handleDeactivate}
              />
            ))
          )}
        </div>
      )}

      {!isLoading && tab === "available" && (
        <div className="space-y-3" data-testid="my-apps-available-list">
          {catalogDisabled ? (
            // Phase A6-prod (Feb 2026) — when HUB_CATALOG_ENDPOINT_ENABLED
            // is OFF (conservative Prod posture), the catalog endpoint
            // returns 404. We render a clean placeholder so users see a
            // friendly "coming soon" message instead of an empty list
            // (which would feel broken). No catalog data is exposed,
            // no apps are activatable from this state.
            <div
              className="rounded-lg border border-slate-200 bg-slate-50 p-6 text-center"
              data-testid="my-apps-catalog-disabled-placeholder"
            >
              <div className="text-base font-medium text-slate-900">
                {i18n.catalogDisabledTitle}
              </div>
              <div className="text-sm text-slate-600 mt-2">
                {i18n.catalogDisabledBody}
              </div>
            </div>
          ) : availableList.length === 0 ? (
            <div className="text-sm text-slate-500" data-testid="my-apps-available-empty">{i18n.emptyAvailable}</div>
          ) : (
            availableList.map((c) => (
              <AvailableCard
                key={c.app_key}
                appCatalog={c}
                isRevoked={revokedKeys.has(c.app_key)}
                i18n={i18n}
                onActivate={handleActivate}
              />
            ))
          )}
        </div>
      )}

      {/* Confirm deactivate dialog */}
      {confirmDeactivate && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center px-4"
          data-testid="my-apps-confirm-deactivate-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5">
            <h3 className="text-sm font-semibold text-slate-900">{i18n.confirmDeactivateTitle}</h3>
            <p className="text-sm text-slate-600 mt-2">{i18n.confirmDeactivateBody}</p>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setConfirmDeactivate(null)}
                className="px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 text-xs hover:bg-slate-50"
                data-testid="my-apps-confirm-deactivate-cancel"
              >
                {i18n.confirmDeactivateCancel}
              </button>
              <button
                type="button"
                onClick={confirmDeactivateNow}
                disabled={busy}
                className="px-3 py-1.5 rounded-md bg-red-600 text-white text-xs hover:bg-red-700 disabled:opacity-50"
                data-testid="my-apps-confirm-deactivate-accept"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 inline animate-spin" /> : i18n.confirmDeactivateAccept}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding wizard */}
      {wizardApp && (
        <OnboardingWizard
          appKey={wizardApp}
          onClose={() => { setWizardApp(null); refresh(); }}
        />
      )}
      </div>
    </PublicLayout>
  );
};

export default MyAppsSettings;
